import React from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'store/hooks';
import { useLocation, useParams } from 'react-router';
import { Box, Button } from '@mui/material';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import ShoppingCart from '@mui/icons-material/ShoppingCart';
import { usePhoneNumberState } from 'components/basket/usePhoneNumberState';
import { ViewBillHeader } from 'components/bill/ui/ViewBill/ViewBillHeader';
import { IAdjustmentLocalResource, OrderScenario } from 'components/order/model/Order';
import { EmptyState } from 'lib/EmptyState';
import { Throbber } from 'lib/Throbber';
import { useAuth } from 'lib/useAuth';
import { useLocalHistory } from 'lib/useLocalHistory';
import { getMenu } from 'store/menu/menuSlice';
import { ApplicationState } from 'store/store';
import { LocationRouteParams, ROUTES } from './routes';
import { resetBasket, updateBasketItemQuantity, updateTimePadding } from 'store/basket/basketSlice';
import { EOrderNonceKey, orderApi } from 'components/order/orderApi';
import { BasketTimeslotPicker } from 'components/basket/BasketTimeslotPicker';
import { InnerPageLayout } from 'lib/InnerPageLayout';
import { InnerPageLayoutContent } from 'lib/InnerPageLayout/InnerPageLayoutContent';
import { ViewBillHeaderInfo } from 'components/bill/ui/ViewBill/ViewBillHeaderInfo';
import { ViewBillContentSection } from 'components/bill/ui/ViewBill/ViewBillContentSection';
import { ConfirmDialog } from 'lib/ConfirmDialog';
import { ViewBillCheckoutPhoneNumber } from 'components/basket/ViewBillCheckoutPhoneNumber';
import { ViewBillCheckoutNoteList } from 'components/bill/ui/ViewBill/Checkout/ViewBillCheckoutNoteList';
import { ViewBillCheckoutTip } from 'components/bill/ui/ViewBill/Checkout/Tip';
import { ViewBillPaymentMethod } from 'components/bill/ui/ViewBill/Checkout/PaymentMethod';
import { CardPayment } from 'components/basket/CardPayment';
import { GuestCheckout } from 'components/bill/ui/ViewBill/GuestCheckout';
import { BottomDialog } from 'lib/BottomDialog';
import { QuantitySelector } from 'lib/QuantitySelector';
import { ViewBasketPayButton } from 'components/basket/payments/ViewBasketPayButton';
import { ViewBasketPaymentInfo } from 'components/basket/ViewBasketPaymentInfo';
import { usePlaceOrder } from 'components/basket/hooks/usePlaceOrder';
import { BasketItems } from 'components/basket/BasketItems';
import { useCalculateBasket } from 'components/basket/hooks/useCalculateBasket';
import { useGiftCards } from 'components/order/hooks/useGiftCards';
import { BasketGiftCard } from 'components/basket/BasketGiftCard';
import { useOrderGiftcardCalculator } from 'components/order/hooks/usePayGiftcardCalculator';
import { useBasketPaymentMethods } from 'components/basket/hooks/useBasketPaymentMethods';
import { useBasketTips } from 'components/basket/hooks/useBasketTips';
import { useBasketTipSettings } from 'components/basket/hooks/useBasketTipSettings';
import { ExistingSharedBasketData, getBasketSession, setBasketSession } from 'components/basket/helpers';
import { clearSessionStorage } from 'lib/helpers';
import { useTransactionLimitValidation } from 'components/basket/hooks/useTransactionLimitValidation';
import { BasketCoupons } from 'components/basket/Reward/BasketCoupons';
import { getLocalBasket } from 'components/basket/localStore';
import { useOrderAwards } from 'components/order/hooks/useOrderAwards';
import { ViewBasketAwardList } from 'components/basket/Reward/ViewBasketAwardList';
import { EGAEventName, useGAHelpers } from 'lib/useGAHelpers';
import { useRouteScenario } from 'src/hooks/useRouteScenario';
import { useDeliveryState } from 'components/delivery/hooks/useDeliveryState';
import { DeliveryConfirmDialog } from 'components/delivery/DeliveryConfirmDialog';
import { DeliveryNote } from 'components/delivery/forms/DeliveryNote';
import { useDeliveryNote } from 'components/delivery/hooks/useDeliveryNote';
import { BaseAlert } from 'ui/alert/BaseAlert';
import { getDeliveryAddressTitle } from 'components/delivery/helpers';
import { BasketSuggestions } from 'components/basket/BasketSuggestions';
import { SuggestionsProvider } from 'src/hooks/useSuggestionsContext';
import { IItemReadResourceV10 } from 'components/basket/model/Basket';
import { isUndefined } from '@pepperhq/types-sdk';
import { isDefined, isEmptyString } from 'lib/typeInference';

const PREFIX = 'ViewBasketPage';

const classes = {
    pageContainer: `${PREFIX}pageContainer`,
    basketError: `${PREFIX}basketError`,
    emptyBasket: `${PREFIX}emptyBasket`
};

const StyledBox = styled(Box)(({ theme }) => ({
    height: '100%',
    width: '100%',
    [`& .${classes.pageContainer}`]: {
        display: 'flex',
        height: '100%',
        paddingBottom: theme.spacing(7),
        overflowY: 'hidden',
        alignItems: 'center'
    },

    [`& .${classes.basketError}`]: {
        fontSize: theme.spacing(12),
        marginBottom: theme.spacing(1),
        marginTop: theme.spacing(-6)
    },

    [`& .${classes.emptyBasket}`]: {
        fontSize: theme.spacing(14),
        marginBottom: theme.spacing(2),
        marginTop: theme.spacing(-6) // Fix appbar screen middle offset
    }
}));

export const ViewBasketPage: React.FC = () => {
    const { t } = useTranslation();

    const dispatch = useAppDispatch();
    const { locationId, merchantId } = useParams<LocationRouteParams>();
    const { search } = useLocation();
    const { push, replace } = useLocalHistory();
    const basket = useSelector((state: ApplicationState) => state.basket);
    const { currentLocation } = useSelector((state: ApplicationState) => state.locations);
    const { settings } = useSelector((state: ApplicationState) => state.settings);
    const { lastRequest } = useSelector((state: ApplicationState) => state.request);
    const { card, isCardLoading } = useAuth();
    const [scenarioFromRoute] = useRouteScenario();
    const scenario = React.useMemo(() => scenarioFromRoute ?? OrderScenario.PREORDER, [scenarioFromRoute]);
    const [deliveryLocation, checkId] = React.useMemo(() => {
        const localBasket = getLocalBasket();
        return [
            basket?.deliveryLocation ?? localBasket?.deliveryLocation,
            basket?.checkId ?? localBasket?.checkId
        ];
    }, [basket?.checkId, basket?.deliveryLocation]);
    const [existingParams, setExistingParams] = React.useState<ExistingSharedBasketData>(
        () => getBasketSession(merchantId, locationId, checkId, deliveryLocation) ?? {}
    );

    const { selectedAddress } = useDeliveryState();
    const [deliveryNote, onDeliveryNoteChange] = useDeliveryNote(selectedAddress?.note);

    const {
        giftCard,
        giftCardBalance,
        giftCardError,
        giftCardLoading,
        gifcardPartiallyRedeemable,
        giftCardFreshlyApplied,
        submitGiftCard,
        resetGiftCard,
        resetGiftCardErrors,
        confirmGiftcardOverpayment
    } = useGiftCards(existingParams.giftCard);

    const { logUserEvent } = useGAHelpers();
    const {
        value: phoneNumber,
        error: phoneNumberError,
        onChange: handlePhoneNumberChange,
        validatePhoneNumber,
        phoneCaptureEnabled
    } = usePhoneNumberState();
    const [menuIsReady, setMenuIsReady] = React.useState(false);
    const { calculatedBasket, refreshBasket, loading, coupons, awards, errorMessage } = useCalculateBasket(
        menuIsReady,
        scenario,
        existingParams,
        selectedAddress
    );

    const { availableAwards, selectedAdjustments, handleSetReward, handleClearRewards, awardsEnabled } =
        useOrderAwards(
            calculatedBasket?.availableAdjustments,
            calculatedBasket?.adjustments,
            calculatedBasket?.total?.total ?? 0,
            existingParams,
            awards.value,
            awards.platformAwardsEnabled
        );
    const totalAfterReward = React.useMemo(() => {
        if (!calculatedBasket) {
            return 0;
        }
        const selectedReward: undefined | IAdjustmentLocalResource = selectedAdjustments?.at(-1);
        if (awards.platformAwardsEnabled || !selectedReward) {
            return calculatedBasket.total.total;
        }
        const afterRewardIsApplied = calculatedBasket.total.total + (selectedReward?.totalValue ?? 0);
        if (afterRewardIsApplied < 0) {
            return 0;
        }
        return afterRewardIsApplied;
    }, [awards.platformAwardsEnabled, calculatedBasket, selectedAdjustments]);
    const { tipAmount, userSelectedTipValue, onSubmitTip, onUseTipAsDefaultChange, useTipAsDefault } =
        useBasketTips(totalAfterReward, scenario, existingParams, giftCard ? giftCardBalance : 0);
    const {
        selectedPaymentOption,
        onCardAdded,
        addNewCardOpen,
        onAddNewCardClose,
        onAddNewCardOpen,
        onCardChange
    } = useBasketPaymentMethods();

    const [openClearBasketDialog, setOpenClearBasketDialog] = React.useState(false);

    const [quantitySelectProductRefId, setQuantitySelectProductRefId] = React.useState<string | undefined>(
        undefined
    );
    const [quantity, setQuantity] = React.useState(0);

    const userCards = React.useMemo(() => (card ? [card] : []), [card]);
    const phoneNumberWrapperRef = React.useRef<HTMLDivElement>(null);
    const scrollToPhoneNumberInput = React.useCallback(() => {
        if (phoneNumberWrapperRef.current) {
            phoneNumberWrapperRef.current.scrollIntoView({ behavior: 'smooth' });
            const [firstInputInsideWrapper] = phoneNumberWrapperRef.current.getElementsByTagName('input');
            if (firstInputInsideWrapper) {
                firstInputInsideWrapper.focus();
            }
        }
    }, []);

    React.useEffect(() => {
        const loadMenu = async () => {
            await dispatch(
                getMenu({
                    locationId: locationId || '',
                    date: basket.timeSlot?.start,
                    scenario,
                    basketChangeMessage: t('DIALOG_TIMESLOT_CHANGED_MESSAGE'),
                    deliveryLocation
                })
            ).unwrap();
            setMenuIsReady(true);
        };
        loadMenu();
    }, [dispatch, locationId, scenario, basket.timeSlot?.start, t, deliveryLocation]);

    const handleOnBack = React.useCallback(() => {
        push(ROUTES.MENU, {}, search);
    }, [push, search]);
    const { isTippingEnabled, tippingToUse } = useBasketTipSettings(scenario);

    const handleErrorDialogClose = React.useCallback(() => {
        dispatch(resetBasket());
        orderApi.resetNonceByKey([EOrderNonceKey.CREATE_ORDER]);
        push(ROUTES.BASE);
    }, [dispatch, push]);

    const handleCloseQuantitySelect = React.useCallback(() => {
        setQuantitySelectProductRefId(undefined);
    }, []);
    const handleOpenQuantitySelect = React.useCallback(
        (referenceId: string | undefined) => {
            const itemIndex = calculatedBasket?.items.findIndex(item => item.referenceId === referenceId);
            if (itemIndex === -1 || isUndefined(itemIndex)) {
                return;
            }
            setQuantity(calculatedBasket?.items[itemIndex].quantity ?? 0);
            setQuantitySelectProductRefId(referenceId);
        },
        [calculatedBasket]
    );
    const handleUpdateItemQuantity = React.useCallback(() => {
        dispatch(updateBasketItemQuantity(quantitySelectProductRefId, quantity));
        dispatch(updateTimePadding());
        handleCloseQuantitySelect();
    }, [quantitySelectProductRefId, quantity, dispatch, handleCloseQuantitySelect]);
    const handleClickItem = React.useCallback(
        (item: IItemReadResourceV10) => {
            handleOpenQuantitySelect(item.referenceId);
        },
        [handleOpenQuantitySelect]
    );

    const handleAwardAdd = React.useCallback(
        (awardId: string) => {
            if (awards.platformAwardsEnabled) {
                const awardToAdd = availableAwards.find(item => item.awardId === awardId);
                awards.onAwardAdd({
                    type: awardToAdd?.type,
                    awardId: awardToAdd?.awardId,
                    quantity: awardToAdd?.quantity
                });
            } else {
                handleSetReward(awardId);
            }
        },
        [availableAwards, awards, handleSetReward]
    );

    const handleAwardsClear = React.useCallback(() => {
        logUserEvent(EGAEventName.AwardsClearClick);
        if (awards.platformAwardsEnabled) {
            awards.onClear();
        } else {
            handleClearRewards();
        }
    }, [awards, handleClearRewards, logUserEvent]);

    const handleClickBack = React.useCallback(() => {
        push(ROUTES.MENU, {}, search);
    }, [push, search]);

    const handleOpenClearBasketDialog = React.useCallback(() => {
        setOpenClearBasketDialog(!openClearBasketDialog);
    }, [openClearBasketDialog]);

    const handleClearBasket = React.useCallback(() => {
        dispatch(resetBasket());
        push(ROUTES.MENU, {}, search);
    }, [dispatch, push, search]);

    const { giftCardPayment, totalValue, giftCardOverpayment } = useOrderGiftcardCalculator(
        totalAfterReward,
        gifcardPartiallyRedeemable,
        tipAmount,
        giftCard,
        giftCardBalance
    );

    const deliveryAddressResource = React.useMemo(() => {
        if (scenario === OrderScenario.DELIVERY && selectedAddress) {
            return {
                ...selectedAddress,
                note: deliveryNote
            };
        }
        return undefined;
    }, [deliveryNote, scenario, selectedAddress]);

    const {
        isPlacingOrder,
        isConfirmationOpen,
        isLocationNoLongerOpen,
        handleToggleConfirmationDialog,
        submitOrder,
        submiteJudoDigitalOrder,
        submitDigitalOrder,
        onDeliveryConfirm,
        onDeliveryCancel,
        onDeliveryQuoteExpired
    } = usePlaceOrder(
        totalValue,
        tipAmount,
        useTipAsDefault,
        userSelectedTipValue,
        selectedPaymentOption,
        scrollToPhoneNumberInput,
        validatePhoneNumber,
        onAddNewCardOpen,
        phoneNumber,
        selectedAdjustments,
        coupons.value,
        giftCardPayment,
        deliveryAddressResource,
        calculatedBasket?.delivery?.quote
    );

    React.useEffect(() => {
        if (merchantId) {
            clearSessionStorage(merchantId, true);
            const params: ExistingSharedBasketData = {
                selectedRewards: selectedAdjustments,
                tipValue: tipAmount,
                giftCard,
                userSelectedTipValue,
                coupons: coupons.value
            };
            setExistingParams(params);
            setBasketSession(params, merchantId, locationId, basket?.checkId, basket?.deliveryLocation);
        }
    }, [
        selectedAdjustments,
        giftCard,
        merchantId,
        locationId,
        tipAmount,
        userSelectedTipValue,
        basket?.checkId,
        basket?.deliveryLocation,
        coupons.value
    ]);

    const handleConfirm = React.useCallback(() => {
        if (lastRequest) {
            handleToggleConfirmationDialog();
            lastRequest();
        }
    }, [handleToggleConfirmationDialog, lastRequest]);
    const handleValidatePhoneNumber = React.useCallback(() => {
        const phoneNumberValid = validatePhoneNumber();
        if (!phoneNumberValid) {
            scrollToPhoneNumberInput();
            throw new Error('The phone number is invalid');
        }
    }, [scrollToPhoneNumberInput, validatePhoneNumber]);
    const handleQuoteExpired = React.useCallback(() => {
        if (onDeliveryQuoteExpired) {
            onDeliveryQuoteExpired();
            refreshBasket();
        }
    }, [onDeliveryQuoteExpired, refreshBasket]);

    const {
        isOutOfLimit,
        handleExceededLimitDialogClose,
        handleValidateTransactionLimit,
        showExceededLimitDialog,
        transactionLimitDescription
    } = useTransactionLimitValidation(totalValue);

    const readyToPay = React.useMemo(
        () => ((!phoneNumberError && !!phoneNumber) || !phoneCaptureEnabled) && !isOutOfLimit,
        [isOutOfLimit, phoneCaptureEnabled, phoneNumber, phoneNumberError]
    );

    const handleValidateOrder = React.useCallback(() => {
        handleValidatePhoneNumber();
        handleValidateTransactionLimit();
    }, [handleValidatePhoneNumber, handleValidateTransactionLimit]);

    const handleLocationNoLongerOpen = React.useCallback(() => {
        switch (scenario) {
            case OrderScenario.DELIVERY:
                replace(ROUTES.DELIVERY.ENTRY);
                break;
            case OrderScenario.DINE_IN:
            case OrderScenario.ORDER_TO_TABLE:
                replace(ROUTES.JOURNEY.LANDING);
                break;
            case OrderScenario.PREORDER:
            default:
                replace(ROUTES.BASE);
        }
    }, [replace, scenario]);

    const showTipping = React.useMemo(
        () =>
            !!settings && !!isTippingEnabled && (!settings?.app?.hideTippingWhenPaymentZero || !!totalValue),
        [isTippingEnabled, settings, totalValue]
    );

    return (
        <StyledBox className="hidden-scroll">
            {loading && !calculatedBasket && (
                <Box height="100%" display="flex" flexDirection="column">
                    <ViewBillHeader title={t('CHECKOUT_TITLE')} onBack={handleOnBack} />
                    <Box flex={1}>
                        <Throbber text={t('GENERAL_LOADING')} />
                    </Box>
                </Box>
            )}
            {calculatedBasket && calculatedBasket.items.length > 0 && (
                <>
                    <BasketTimeslotPicker />
                    <InnerPageLayout disableContentSpaces>
                        <ViewBillHeader onBack={handleClickBack} title={t('BILL_CHECKOUT_TITLE')} />
                        <InnerPageLayoutContent>
                            <Throbber
                                isLoading={loading || isCardLoading}
                                text={t('GENERAL_LOADING')}
                                isOverlay
                            />
                            <Box marginBottom={2}>
                                <ViewBillHeaderInfo
                                    locationVisible={false}
                                    address={getDeliveryAddressTitle(selectedAddress)}
                                    dropoff={calculatedBasket?.delivery?.eta?.dropoff}
                                    dropoffDeadline={calculatedBasket?.delivery?.eta?.dropoffDeadline}
                                />
                            </Box>
                            <BasketItems
                                calculatedBasket={calculatedBasket}
                                onItemClick={handleClickItem}
                                onOpenClearBasketDialog={handleOpenClearBasketDialog}
                            />
                            {openClearBasketDialog && (
                                <ConfirmDialog
                                    open={openClearBasketDialog}
                                    onClose={handleOpenClearBasketDialog}
                                    onConfirm={handleClearBasket}
                                    title={t('CHECKOUT_EMPTY_BASKET_TITLE')}
                                    description={t('CHECKOUT_EMPTY_ALERT_TITLE')}
                                    confirmMessage={t('CHECKOUT_EMPTY_ALERT_YES')}
                                    cancelMessage={t('CHECKOUT_EMPTY_ALERT_NO')}
                                />
                            )}
                            <SuggestionsProvider>
                                <BasketSuggestions loading={loading} items={calculatedBasket.items} />
                            </SuggestionsProvider>
                            {!!awardsEnabled && (
                                <ViewBasketAwardList
                                    selected={selectedAdjustments}
                                    onSelect={handleAwardAdd}
                                    onUnselect={awards.onAwardRemove}
                                    onClear={handleAwardsClear}
                                    availableAwards={availableAwards}
                                    showAddMoreButton={awards.showMoreAwardsButton}
                                    onAddMore={awards.onMultipleAwardsEnable}
                                    multi={awards.platformAwardsEnabled}
                                />
                            )}
                            {coupons.showCoupons && (
                                <BasketCoupons
                                    onRemove={coupons.onCouponRemove}
                                    onAdd={coupons.onCouponAdd}
                                    coupons={coupons.couponAdjustments}
                                    loading={loading}
                                />
                            )}
                            {phoneCaptureEnabled && (
                                <ViewBillCheckoutPhoneNumber
                                    value={phoneNumber}
                                    onChange={handlePhoneNumberChange}
                                    error={phoneNumberError}
                                    ref={phoneNumberWrapperRef}
                                />
                            )}

                            <ViewBillCheckoutNoteList />
                            {!!showTipping && !!tippingToUse && (
                                <ViewBillContentSection
                                    title={t('BILL_CHECKOUT_TIPS_TITLE')}
                                    subtitle={t('BILL_CHECKOUT_TIPS_BODY')}
                                    bottomSpacing={2}
                                >
                                    <ViewBillCheckoutTip
                                        useTipAsDefault={useTipAsDefault}
                                        onUseTipAsDefaultChange={onUseTipAsDefaultChange}
                                        total={totalAfterReward}
                                        tipping={tippingToUse}
                                        onTipUpdate={onSubmitTip}
                                        currencySymbol={settings?.region?.currencySymbol}
                                        userDefaultSelectedValue={userSelectedTipValue}
                                    />
                                </ViewBillContentSection>
                            )}
                            <ViewBillContentSection
                                title={t('BILL_CHECKOUT_PAYMENT_METHOD_TITLE')}
                                bottomSpacing={2}
                            >
                                <ViewBillPaymentMethod
                                    cards={userCards}
                                    selectedPaymentMethod={selectedPaymentOption}
                                    onPaymentMethodChange={onCardChange}
                                    onAddNewCardClick={onAddNewCardOpen}
                                />
                            </ViewBillContentSection>
                            {settings?.giftCardPaymentEnabled && (
                                <ViewBillContentSection bottomSpacing={2}>
                                    <BasketGiftCard
                                        balance={giftCardBalance}
                                        error={giftCardError}
                                        loading={giftCardLoading}
                                        onGiftCardApply={submitGiftCard}
                                        giftCardValue={giftCard}
                                        reset={resetGiftCard}
                                        resetErrors={resetGiftCardErrors}
                                        showOverpayment={giftCardFreshlyApplied && giftCardOverpayment}
                                        onAcceptOverpayment={confirmGiftcardOverpayment}
                                        orderBalance={totalAfterReward}
                                    />
                                </ViewBillContentSection>
                            )}

                            {scenario === OrderScenario.DELIVERY && selectedAddress && (
                                <ViewBillContentSection
                                    bottomSpacing={2}
                                    title={t('BILL_CHECKOUT_DELIVERY_DRIVER_NOTES')}
                                >
                                    <DeliveryNote value={deliveryNote} onChange={onDeliveryNoteChange} />
                                </ViewBillContentSection>
                            )}

                            <ViewBasketPaymentInfo
                                tip={tipAmount}
                                scenario={scenario}
                                calculatedBasket={calculatedBasket}
                                total={totalValue}
                                giftCardPayment={giftCardPayment}
                                selectedAwards={selectedAdjustments}
                                multi={awards.platformAwardsEnabled}
                            />

                            <ViewBasketPayButton
                                totalToPay={totalValue}
                                selectedPaymentOption={selectedPaymentOption}
                                readyToPay={readyToPay}
                                onIntegratedWalletsPay={submiteJudoDigitalOrder}
                                onWalletsPay={submitDigitalOrder}
                                onCardPay={submitOrder}
                                loading={isPlacingOrder}
                                validate={handleValidateOrder}
                            />
                        </InnerPageLayoutContent>
                    </InnerPageLayout>
                    <CardPayment
                        open={addNewCardOpen}
                        handleCreateOrder={onCardAdded}
                        onClose={onAddNewCardClose}
                        isPlacingOrder={isPlacingOrder}
                    />
                    <GuestCheckout onClickBack={handleClickBack} />
                    <BottomDialog
                        open={
                            isDefined(quantitySelectProductRefId) &&
                            !isEmptyString(quantitySelectProductRefId)
                        }
                        onClose={handleCloseQuantitySelect}
                    >
                        <Box
                            width="100%"
                            height="100%"
                            display="flex"
                            alignItems="center"
                            flexDirection="column"
                        >
                            <QuantitySelector min={0} max={999} quantity={quantity} onChange={setQuantity} />

                            <Button
                                fullWidth
                                variant="contained"
                                color="primary"
                                onClick={handleUpdateItemQuantity}
                            >
                                {quantity === 0
                                    ? t('BASKET_REMOVE_ITEM')
                                    : t('PREORDER_UPDATE_BASKET_QUANTITY')}
                            </Button>
                        </Box>
                    </BottomDialog>
                    <ConfirmDialog
                        open={isConfirmationOpen}
                        disableBackdropClick
                        disableEscapeKeyDown
                        onClose={handleErrorDialogClose}
                        confirmMessage={t('RETRY')}
                        onConfirm={handleConfirm}
                        description={t('DIALOG_ORDER_STATUS_UNKNOWN')}
                    />
                    <ConfirmDialog
                        open={showExceededLimitDialog}
                        disableBackdropClick
                        disableEscapeKeyDown
                        isCancellable={false}
                        onClose={handleExceededLimitDialogClose}
                        confirmMessage={t('OK')}
                        onConfirm={handleExceededLimitDialogClose}
                        description={transactionLimitDescription}
                        title={t('DIALOG_OOPS')}
                        align="center"
                    />
                    <DeliveryConfirmDialog
                        open={!!onDeliveryConfirm}
                        onCancel={onDeliveryCancel}
                        onSubmit={onDeliveryConfirm}
                        from={currentLocation?.title}
                        to={calculatedBasket?.delivery?.dropoffAddress.address}
                        dropoff={calculatedBasket?.delivery?.eta?.dropoff}
                        dropoffDeadline={calculatedBasket?.delivery?.eta?.dropoffDeadline}
                    />
                    <BaseAlert
                        open={!!onDeliveryQuoteExpired}
                        onCancel={handleQuoteExpired}
                        disableBackdropClick
                        buttonLabel={t('DELIVERY_QUOTE_EXPIRED_BUTTON')}
                        title={t('DELIVERY_QUOTE_EXPIRED_TITLE')}
                        content={t('DELIVERY_QUOTE_EXPIRED_MESSAGE')}
                    />
                    <BaseAlert
                        open={!!isLocationNoLongerOpen}
                        onCancel={handleLocationNoLongerOpen}
                        disableBackdropClick
                        content={t('LOCATION_NO_LONGER_OPEN_MESSAGE')}
                    />
                </>
            )}
            {!loading && !calculatedBasket && basket.items.length !== 0 && (
                <Box height="100%" display="flex" flexDirection="column">
                    <ViewBillHeader title={t('CHECKOUT_TITLE')} onBack={handleOnBack} />
                    <EmptyState
                        headerText={t('GENERAL_GENERIC_ERROR')}
                        paragraphText={
                            errorMessage ||
                            t('BASKET_ERROR_LOADING', {
                                errorMessage: 'An unknown error has occurred'
                            })
                        }
                    >
                        <ErrorOutline className={classes.basketError} />
                    </EmptyState>
                </Box>
            )}
            {!loading &&
                ((!calculatedBasket && basket.items.length === 0) ||
                    (!!calculatedBasket && calculatedBasket.items.length === 0)) && (
                    <Box
                        height="100%"
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="center"
                    >
                        <ViewBillHeader title={t('CHECKOUT_TITLE')} onBack={handleOnBack} />
                        <EmptyState
                            headerText={t('DIALOG_BASKET_EMPTY_TITLE')}
                            paragraphText={t('DIALOG_BASKET_EMPTY_BODY')}
                        >
                            <ShoppingCart className={classes.emptyBasket} />
                        </EmptyState>
                    </Box>
                )}
        </StyledBox>
    );
};
